import { useState } from 'react';
import { cheapest } from '../../lib/catalog';
import { fmtCount, fmtReq, usdCost } from '../../lib/format';
import { STARTER_BALANCE_USD, starterBalanceUsd } from '../../lib/signupOffer';
import { pluralUnit, quotedRateLabel, quotedRateUsd, type DisplayPrice } from '../../lib/pricing';
import type { Endpoint } from '../../features/catalog/model';
import { m } from '../../paraglide/messages';

/** Monthly volumes the estimate offers, taken from the lobstr.io "Pricing
 *  Simulator" reference (100 / 10K / 100K / 1M results). */
export const VOLUME_TICKS = [100, 10_000, 100_000, 1_000_000] as const;

/** The volume the estimate server-renders, so the HTML states a real figure. */
const DEFAULT_VOLUME = 10_000;

/** The price every estimate uses: the endpoint's cheapest serving lane. */
export const estimatePrice = (endpoint: Endpoint): DisplayPrice => cheapest(endpoint).price;

/** What the count is of: requests on a flat price, the billed unit on a metered one. */
export const volumeNoun = (price: DisplayPrice): string =>
  price.metered && price.unit ? pluralUnit(price.unit) : m.endpoint_cost_requests();

/**
 * USD for `count` of the price's own denomination. Owner inputs: the per-1,000
 * rate is `quotedRateUsd` in lib/pricing (per request when flat, per billed unit
 * at the offer's cap when metered); this only scales it to the count.
 */
export const volumeCostUsd = (price: DisplayPrice, count: number): number =>
  (quotedRateUsd(price) / 1000) * count;

/** Whole requests the starting balance pays for, at the per-request price
 *  (the ceiling on a metered offer, so the count is a floor). */
const freeRequests = (price: DisplayPrice): number =>
  Math.floor(STARTER_BALANCE_USD / price.perRequestUsd);

const [MIN_VOLUME] = VOLUME_TICKS;
const MAX_VOLUME = VOLUME_TICKS[VOLUME_TICKS.length - 1];

/** A count held inside the ticks' range, for placing it on the slider. */
const clampVolume = (count: number): number =>
  Math.min(MAX_VOLUME, Math.max(MIN_VOLUME, Math.round(count)));

/** Slider position (0..1) on a log scale spanning the ticks. */
export const volumeToPosition = (count: number): number =>
  (Math.log10(clampVolume(count)) - Math.log10(MIN_VOLUME)) /
  (Math.log10(MAX_VOLUME) - Math.log10(MIN_VOLUME));

/** Inverse of `volumeToPosition`, rounded to two significant figures so the
 *  slider lands on counts a person would type. */
export const positionToVolume = (position: number): number => {
  const raw = 10 ** (Math.log10(MIN_VOLUME) + position * (Math.log10(MAX_VOLUME) - Math.log10(MIN_VOLUME)));
  const step = 10 ** (Math.floor(Math.log10(raw)) - 1);
  return clampVolume(Math.round(raw / step) * step);
};

/** `10000` -> `10K`, for tick labels only. */
export const tickLabel = (count: number): string =>
  count >= 1_000_000 ? `${count / 1_000_000}M` : count >= 1_000 ? `${count / 1_000}K` : String(count);

/** The published rate as the provider table shows it, e.g. `$0.80/1k req`. */
export const rateText = (price: DisplayPrice): string =>
  `${usdCost(quotedRateUsd(price))}${quotedRateLabel(price)}`;

/** The starting-balance line, or null when the balance does not cover one request. */
export const freeLine = (price: DisplayPrice): string | null => {
  const n = freeRequests(price);
  if (n < 1) return null;
  const inputs = { count: fmtReq(n), balance: starterBalanceUsd() };
  return price.metered ? m.endpoint_cost_free_metered(inputs) : m.endpoint_cost_free(inputs);
};

/** A metered offer's per-request ceiling, or null on a flat price. */
export const ceilingLine = (price: DisplayPrice): string | null =>
  price.metered && price.itemsToCap !== undefined
    ? m.endpoint_cost_ceiling({ cost: usdCost(price.perRequestUsd), count: fmtReq(price.itemsToCap), noun: volumeNoun(price) })
    : null;

/**
 * A request count a person can type or pick. `text` is the input's own draft
 * while it has focus, so reformatting never moves the caret mid-edit.
 */
export const useVolume = () => {
  const [count, setCountState] = useState<number>(DEFAULT_VOLUME);
  const [draft, setDraft] = useState<string | null>(null);
  return {
    count,
    text: draft ?? fmtCount(count),
    setCount: (next: number) => {
      setDraft(null);
      setCountState(next);
    },
    onText: (text: string) => {
      setDraft(text);
      setCountState(Number(text.replace(/[^0-9]/g, '')));
    },
    onBlur: () => setDraft(null),
  };
};
